import React from 'react';
import { cn } from '../../../lib/cn';

/**
 * OptionCard, the fat survey answer card.
 *
 * CSS selector: `mobile/organisms.css` `.ds-option-card` (+ `--selected`,
 * `__check`). The CSS lives in the organisms layer, which another workstream
 * owns; this file only reads it. The component sits with the molecules because
 * it is a single-job composition, and because the registry lists it as one of
 * the two components the library was missing entirely.
 *
 * Doctrine: ink is command, accent is state, and selecting an answer commits
 * you to it, so a selected card inverts to near-black rather than picking up an
 * accent tint. It is white at rest and inverts on selection, and the polarity
 * flips automatically in dark theme because both sides come from the ink pair.
 *
 * The checkmark renders on multi-select only. On a single-select deck the
 * inversion already says which one is chosen, and a tick would imply you can
 * pick more than one. That is what `multi` controls, and it is the whole reason
 * the CSS defines `__check` as a separate element.
 *
 * Renders a real button, with `aria-pressed` on multi-select decks and
 * `aria-checked` with `role="radio"` on single-select ones, so the deck
 * announces the same choice model it looks like.
 */
function CheckGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export const OptionCard = React.forwardRef(function OptionCard(
  { className, selected = false, multi = false, type = 'button', children, ...rest },
  ref,
) {
  const a11y = multi
    ? { 'aria-pressed': selected }
    : { role: 'radio', 'aria-checked': selected };

  return (
    <button
      ref={ref}
      type={type}
      className={cn('ds-option-card', selected && 'ds-option-card--selected', className)}
      {...a11y}
      {...rest}
    >
      <span>{children}</span>
      {multi && selected ? (
        <span className="ds-option-card__check">
          <CheckGlyph />
        </span>
      ) : null}
    </button>
  );
});

export default OptionCard;
